import { createFunction, getExpressionForValidation, getRangeExpression, ValidationCache } from '@msdk/common'
import {
  BaseValidationDefinition,
  CONTROL_VALIDATION_MAPPER,
  Entity,
  EntityContext,
  FieldRange,
  FieldSelectOptions,
  findNode,
  FormNode,
  formTraverse,
  KeepOptionValidationDefinition,
  ListValue,
  LogicalPath,
  NumericFieldDefinition,
  NumericValue,
  RangeValidationDefinition,
  readUserData,
  RemoveOptionValidationDefinition,
  TenantPath,
  tryConvert,
  tryConvertDecimalInfo,
  UserPath,
  ValidationDefinition,
} from '@msdk/definitions'

import { distinct } from '../utils'

export function runValidations(root: FormNode, entity: EntityContext, validationCache: ValidationCache) {
  formTraverse(root, (node) => {
    const validationsForNode = validationCache.cachedRules[node.id] || []
    validationsForNode.forEach((v) => {
      const func = getFunc(v, node, validationCache.cachedFunctions)
      const res = evaluate(entity, func)
      node.value.validationState = 'Valid'
      node.value.validationFlags |= CONTROL_VALIDATION_MAPPER[v.validationType]
      if (res) {
        execute(v, node)
      }
    })
    execRanges(node, validationsForNode, entity)
  })
}

export function runListValidations(root: FormNode, entity: EntityContext, validationCache: ValidationCache) {
  Object.keys(validationCache.cachedListRules).forEach((k) => {
    const validations = validationCache.cachedListRules[k]
    const listName = validations[0].onList as string
    const listValue = entity.fields[listName] as ListValue
    if (listValue?.value?.length) {
      listValue.value.forEach((row) => {
        const rowNode = findNode(root, row.id) as FormNode
        const rowContext = new EntityContext(row, root, [])
        formTraverse(rowNode.firstChild, (fieldNode) => {
          const validationsForField = validations.filter((v) => v.field === fieldNode.id)
          validationsForField.forEach((v) => {
            const func = getFunc(v, rowNode, validationCache.cachedFunctions, true)
            const res = evaluate(entity, func, rowContext)
            fieldNode.value.validationState = 'Valid'
            fieldNode.value.validationFlags |= CONTROL_VALIDATION_MAPPER[v.validationType]
            if (res) {
              execute(v, fieldNode)
            }
          })
          execRanges(fieldNode, validationsForField, entity, rowContext)
        })
      })
    }
  })

  Object.keys(validationCache.cachedOnOneRowOfListRules).forEach((k) => {
    const validations = validationCache.cachedOnOneRowOfListRules[k]
    const listName = validations[0].onOneRowOfList as string
    const listValue = entity.fields[listName] as ListValue
    if (listValue?.value?.length) {
      const rows = listValue.value as Entity[]
      validations.forEach((v) => {
        let result = false
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i]
          const rowNode = findNode(root, row.id) as FormNode
          const rowContext = new EntityContext(row, root, [])
          const func = getFunc(v, rowNode, validationCache.cachedFunctions, true)
          const res = evaluate(entity, func, rowContext)
          if (res) {
            result = true
            break
          }
        }

        if (result) {
          const fieldNode = findNode(root, v.field) as FormNode
          execute(v, fieldNode)
        }
      })
    }
  })

  Object.keys(validationCache.cachedOnAllRowsOfList).forEach((k) => {
    const validations = validationCache.cachedOnAllRowsOfList[k]
    const listName = validations[0].onAllRowsOfList as string
    const listValue = entity.fields[listName] as ListValue
    if (listValue?.value?.length) {
      const rows = listValue.value as Entity[]
      validations.forEach((v) => {
        let result = true
        rows.forEach((row) => {
          const rowNode = findNode(root, row.id) as FormNode
          const rowContext = new EntityContext(row, root, [])
          const func = getFunc(v, rowNode, validationCache.cachedFunctions, true)
          const res = evaluate(entity, func, rowContext)
          result = result && res
        })

        if (result) {
          const fieldNode = findNode(root, v.field) as FormNode
          execute(v, fieldNode)
        }
      })
    }
  })
}
// eslint-disable-next-line @typescript-eslint/ban-types
function evaluate(context: EntityContext, func: Function, rowContext?: EntityContext): boolean {
  const res = func({
    entity: context,
    row: rowContext,
    logic: new LogicalPath(),
    user: new UserPath(),
    tenant: new TenantPath(),
  })
  return res
}

function execute(v: BaseValidationDefinition, fieldNode: FormNode): void {
  switch (v.validationType) {
    case 'RemoveOption': {
      const remove = v as RemoveOptionValidationDefinition
      const toRemove = remove.option.split(',')
      const existing = fieldNode.value.selectOptions?.remove as string[]
      const selectOpts = fieldNode.value.selectOptions as FieldSelectOptions
      selectOpts.remove = existing.concat(toRemove).filter(distinct)
      break
    }
    case 'KeepOption': {
      const keep = v as KeepOptionValidationDefinition
      const toKeep = keep.option.split(',')
      const existing = fieldNode.value.selectOptions?.keep as string[]
      const selectOpts = fieldNode.value.selectOptions as FieldSelectOptions
      selectOpts.keep = existing.concat(toKeep).filter(distinct)
      break
    }
    case 'Range':
    case 'Expression':
    case 'Mandatory':
      fieldNode.value.validationMessages.push(v.msgCode)
      fieldNode.value.validationState = 'Invalid'
      if (v.msgParameters) {
        fieldNode.value.validationParams = v.msgParameters.split(',')
      }
      break
    default:
      throw new Error('Unsupported validation type: ' + v.validationType)
  }
}

function getFunc(
  v: BaseValidationDefinition,
  node: FormNode,
  // eslint-disable-next-line @typescript-eslint/ban-types
  cache: Record<string, Function>,
  row?: boolean,
  // eslint-disable-next-line @typescript-eslint/ban-types
): Function {
  let func = cache[getExpressionForValidation(v, row)]
  if (v.validationType === 'Range') {
    const rv = v as RangeValidationDefinition
    const minDifferent = node.value.range?.min !== rv.min
    const maxDifferent = node.value.range?.max !== rv.max

    if (minDifferent || maxDifferent) {
      const range = node.value.range as FieldRange
      const fieldName = node.value.fieldDefinition?.name as string
      const expression = getRangeExpression(range.min, range.max, fieldName, row)
      func = createFunction(expression)
    }
  } else if (v.validationType === 'Expression') {
    const val = v as ValidationDefinition
    // This is the expression the framework uses to handle
    // custom validation on list field rows
    if (val.condition.startsWith('row.id === ')) {
      func = createFunction(val.condition)
    }
  }
  return func
}

/**
 * Ranges that are added from a data rule to a field that did not have any
 * range validation to begin with have to be handled in a specific way.
 * The validation cache does not have any notion of these kind o validation,
 * so whe are creating and executing a validation on the fly.
 */
function execRanges(
  node: FormNode,
  validationsForNode: BaseValidationDefinition[],
  entity: EntityContext,
  row?: EntityContext,
) {
  if (!node.value.range) return

  const rangeValidation = validationsForNode.find((v) => v.validationType === 'Range')

  // it's doing the pass for the custom validations (no rangeValidation),
  // but this one already has a default one
  if (!rangeValidation && node.value.validationMessages?.length > 0) return

  if (rangeValidation) {
    const rv = rangeValidation as RangeValidationDefinition
    // Skip if range hasn't been changed by a DataRule, main validation loop already handled it
    if (node.value.range.min === rv.min && node.value.range.max === rv.max) return
  }

  // Either the range was set only by a DataRule (no static RangeValidation exists),
  // or it was overridden by a DataRule — run the validation using the current range.
  const { range } = node.value
  const expression = getRangeExpression(range.min, range.max, node.id, !!row)
  const func = createFunction(expression)
  const res = evaluate(entity, func, row)
  if (res) {
    const v = createRangeDefinition(node, node.id, range.max, range.min)
    execute(v, node)
  }
}

const getDecimalInfo = (fieldDef: NumericFieldDefinition) => {
  return tryConvertDecimalInfo(fieldDef.decimalInfo, fieldDef.um, fieldDef.entityName, fieldDef.name, fieldDef.step)
}

function createRangeDefinition(node: FormNode, fieldName: string, max: number, min: number) {
  const fieldDef = node.value.fieldDefinition as NumericFieldDefinition
  const minDefined = min !== null && min !== undefined
  const maxDefined = max !== null && max !== undefined

  const lang = readUserData().lang as string

  const convertedMin = minDefined ? tryConvert(min, fieldDef.um, fieldDef.entityName, fieldDef.name) : undefined
  const convertedMax = maxDefined ? tryConvert(max, fieldDef.um, fieldDef.entityName, fieldDef.name) : undefined

  const decimals = getDecimalInfo(fieldDef)?.decimalLimit ?? 0
  const convertedMinNumeric = new NumericValue(convertedMin, undefined, undefined, decimals)
  const convertedMaxNumeric = new NumericValue(convertedMax, undefined, undefined, decimals)

  const v: RangeValidationDefinition = {
    field: fieldName,
    min,
    max,
    msgCode: 'FRM_00006',
    validationType: 'Range',
    msgParameters: `${convertedMinNumeric.toStringForView(lang)},${convertedMaxNumeric.toStringForView(lang)}`,
  }

  if (maxDefined && !minDefined) {
    v.msgCode = 'FRM_00036'
    v.msgParameters = `${convertedMaxNumeric.toStringForView(lang)}`
  }

  if (!maxDefined && minDefined) {
    v.msgCode = 'FRM_00035'
    v.msgParameters = `${convertedMinNumeric.toStringForView(lang)}`
  }

  return v
}
